import { ChangeEvent, useMemo, useState } from "react";
import { faUndo } from "@fortawesome/free-solid-svg-icons/faUndo";
import { resources } from "@constants/hardcoded-strings";
import { BOOLEAN_RADIO_INPUT_OPTIONS } from "@constants/boolean-radio-input-options";
import { TEXT_POSITION_OPTIONS } from "@constants/text-position-options";
import { LabelWidthTypes } from "@enums/label-width-types";
import { LayoutTypes } from "@enums/layout-types";
import { ResourceCategoryTypes } from "@enums/resource-category-types";
import { SelectOption } from "@idw-types/common";
import {
    AXNUIContent,
    AXNUIContentHeader,
    AXNUITheme,
    AXNUIThemeHeader
} from "@iddataweb/idw-common-ui/types";
import { ThemingHeaderValidationErrors } from "@idw-types/common/validation/common/theming-validation-errors"
import { isStringTrue } from "@iddataweb/idw-common-ui/utils";
import { RadioInputFormGroup } from "../../../../../common/radio-input-form-group/radio-input-form-group";
import { InputFormGroup } from "../../../../../common/input-form-group/input-form-group";
import { SelectFormGroup } from "../../../../../common/select-form-group/select-form-group";
import { FileInputFormGroup } from "../../../../../common/file-input-form-group/file-input-form-group";
import { ThemingFormGroupWrapper } from "@containers/common/theming-input-form-group/theming-form-group-wrapper";
import { PageLevelTypes } from "@enums/page-level-types";

type ThemingHeaderLogoSectionProps = {
    layout: LayoutTypes;
    labelWidth: LabelWidthTypes;
    isDisabled: boolean;
    axnuiContent: AXNUIContent;
    axnuiTheme: AXNUITheme;
    inheritedAXNUIContent: AXNUIContent;
    inheritedAXNUITheme: AXNUITheme;
    pageLevel: PageLevelTypes;
    validationErrors: ThemingHeaderValidationErrors;
    uploadFileToS3: (file: File, category: string) => Promise<string>;
    updateAXNUIContentHeaderFieldValue: (key: keyof AXNUIContentHeader, value: string) => void;
    updateAXNUIThemeHeaderFieldValue: (key: keyof AXNUIThemeHeader, value: string | boolean) => void;
};

export const ThemingHeaderLogoSection = (props: ThemingHeaderLogoSectionProps) => {
    const {
        layout,
        labelWidth,
        isDisabled,
        axnuiContent,
        axnuiTheme,
        inheritedAXNUIContent,
        inheritedAXNUITheme,
        pageLevel,
        validationErrors,
        uploadFileToS3,
        updateAXNUIContentHeaderFieldValue,
        updateAXNUIThemeHeaderFieldValue
    } = props;

    const [headerLogoUploadInProgress, setHeaderLogoUploadInProgress] = useState(false);

    const handleAddHeaderLogoChange = (newValue: string) => {
        updateAXNUIThemeHeaderFieldValue("showLogo", isStringTrue(newValue));
    };

    const handleResetAddHeaderLogo = () => {
        updateAXNUIThemeHeaderFieldValue("showLogo", inheritedAXNUITheme.header.showLogo);
    };

    const handleHeaderLogoChange = async (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (files.length > 0) {
            const file = files[0];
            //upload to s3
            setHeaderLogoUploadInProgress(true);
            const uploadResult = await uploadFileToS3(file, ResourceCategoryTypes.HEADER_LOGO);
            if (uploadResult) {
                updateAXNUIThemeHeaderFieldValue("logoUrl", uploadResult);
            }
            setHeaderLogoUploadInProgress(false);
        }
    };

    const handleResetHeaderLogo = () => {
        updateAXNUIThemeHeaderFieldValue("logoUrl", inheritedAXNUITheme.header.logoUrl || "");
    };

    const handleHeaderLogoPositionChange = (newValue: SelectOption) => {
        updateAXNUIThemeHeaderFieldValue("alignLogo", newValue.label);
    };

    const handleResetHeaderLogoPosition = () => {
        updateAXNUIThemeHeaderFieldValue("alignLogo", inheritedAXNUITheme.header.alignLogo);
    };

    const handleHeaderLogoAltTextChange = async (event: ChangeEvent<HTMLInputElement>) => {
        updateAXNUIContentHeaderFieldValue("logoAltText", event.target.value);
    };

    const handleResetHeaderLogoAltText = () => {
        updateAXNUIContentHeaderFieldValue("logoAltText", inheritedAXNUIContent.header.logoAltText);
    };

    const selectedHeaderLogoPositionOption = useMemo(() => {
        if (axnuiTheme.header.alignLogo) {
            return TEXT_POSITION_OPTIONS.find(po => po.label === axnuiTheme.header.alignLogo);
        }
        return null;
    }, [axnuiTheme.header.alignLogo]);

    return (
        <div>
            <ThemingFormGroupWrapper
                inheritedThemingPropertyValue={inheritedAXNUITheme.header.showLogo}
                currentThemingPropertyValue={axnuiTheme.header.showLogo}
                pageLevel={pageLevel}
            >
                <RadioInputFormGroup
                    controlId={`theming-header-add-header-logo-radio-input`}
                    automationId={`theming-header-add-header-logo-radio-input`}
                    label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.headerLogo.inputs.addHeaderLogo.label}
                    layout={layout}
                    labelWidth={labelWidth}
                    onChange={handleAddHeaderLogoChange}
                    options={BOOLEAN_RADIO_INPUT_OPTIONS}
                    value={axnuiTheme.header.showLogo ? "true" : "false"}
                    disabled={isDisabled}
                    isRequired={true}
                    afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.headerLogo.inputs.addHeaderLogo.tooltip}
                    afterLabelIcon={isDisabled ? null : faUndo}
                    afterLabelIconOnClick={handleResetAddHeaderLogo}
                />
            </ThemingFormGroupWrapper>
            {axnuiTheme.header.showLogo && (
                <>
                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedAXNUITheme.header.logoUrl}
                        currentThemingPropertyValue={axnuiTheme.header.logoUrl}
                        pageLevel={pageLevel}
                    >
                        <FileInputFormGroup
                            controlId={"theming-header-header-logo-input"}
                            automationId={"theming-header-header-logo-input"}
                            label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.headerLogo.inputs.headerLogo.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.headerLogo.inputs.headerLogo.label}
                            layout={layout}
                            labelWidth={labelWidth}
                            onChange={handleHeaderLogoChange}
                            value={axnuiTheme.header.logoUrl || ""}
                            accept={"image/png, image/gif, image/jpeg, image/jpg"}
                            disabled={isDisabled}
                            uploadInProgress={headerLogoUploadInProgress}
                            isRequired={true}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.headerLogo.inputs.headerLogo.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetHeaderLogo}
                        />
                    </ThemingFormGroupWrapper>
                    {validationErrors.headerLogoError}
                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedAXNUITheme.header.alignLogo}
                        currentThemingPropertyValue={axnuiTheme.header.alignLogo}
                        pageLevel={pageLevel}
                    >
                        <SelectFormGroup
                            controlId={"theming-header-header-text-postition-select-input"}
                            automationId={"theming-header-header-text-position-select-input"}
                            label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.headerLogo.inputs.headerLogoPosition.label}
                            ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.headerLogo.inputs.headerLogoPosition.label}
                            disabled={isDisabled}
                            isClearable={false}
                            isRequired={true}
                            isSearchable={false}
                            layout={layout}
                            labelWidth={labelWidth}
                            onChange={handleHeaderLogoPositionChange}
                            options={TEXT_POSITION_OPTIONS}
                            selectedOption={selectedHeaderLogoPositionOption}
                            afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.headerLogo.inputs.headerLogoPosition.tooltip}
                            afterLabelIcon={isDisabled ? null : faUndo}
                            afterLabelIconOnClick={handleResetHeaderLogoPosition}
                        />
                    </ThemingFormGroupWrapper>

                    <ThemingFormGroupWrapper
                        inheritedThemingPropertyValue={inheritedAXNUIContent.header.logoAltText}
                        currentThemingPropertyValue={axnuiContent.header.logoAltText}
                        pageLevel={pageLevel}
                    >
                    <InputFormGroup
                        controlId={"theming-header-header-logo-alt-text-input"}
                        automationId={"theming-header-header-logo-alt-text-input"}
                        label={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.headerLogo.inputs.headerLogoAltText.label}
                        ariaLabel={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.headerLogo.inputs.headerLogoAltText.label}
                        layout={layout}
                        labelWidth={labelWidth}
                        onChange={handleHeaderLogoAltTextChange}
                        value={axnuiContent.header.logoAltText || ""}
                        disabled={isDisabled}
                        isRequired={false}
                        afterLabelTooltipText={resources.pages.verificationServiceDetails.tabs.theming.subTabs.header.sections.headerLogo.inputs.headerLogoAltText.tooltip}
                        afterLabelIcon={isDisabled ? null : faUndo}
                        afterLabelIconOnClick={handleResetHeaderLogoAltText}
                    />
                     </ThemingFormGroupWrapper>
                </>
            )}
        </div>
    );
};